import {useMemo} from 'react';

type Props = {
  alt: string;
  url: string;
  classes?: string;
  dataTestId?: string;
  useCache?: boolean; // If set to true it makes use of the useMemo method which reuses existing image instead of reloading
  // When set (e.g. "bg-accent"), the SVG is used as a CSS mask and tinted with the given ODS
  // background token. Single-color icons only. Requires an explicit width AND height in `classes`
  // (a mask has no intrinsic size).
  maskColorClass?: string;
};

// Returns an <img> element that uses a .svg vector as the image's src
// Vector images scale with higher resolutions, allowing quicker renders on higher quality screens without any quality loss
const Image = ({alt, url, classes = '', dataTestId, useCache = true, maskColorClass}: Props): JSX.Element => {
  const src = `${url}.svg`;
  const imgComponent = maskColorClass ? (
    <span
      role="img"
      aria-label={alt}
      data-testid={dataTestId}
      className={`${classes} ${maskColorClass} svg-icon inline-block`}
      style={{
        maskImage: `url("${src}")`,
        WebkitMaskImage: `url("${src}")`,
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
      }}
    />
  ) : (
    <img alt={alt} className={`${classes} svg-icon`} src={src} data-testid={dataTestId} />
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedComponent = useMemo(() => imgComponent, [url, maskColorClass]);
  if (useCache) {
    return memoizedComponent;
  }
  return imgComponent;
};

export default Image;
